import { useMutation, useQuery } from '@tanstack/react-query';
import { supabase } from '../services/supabase';
import { queryClient } from '../queryClient';

export type AppIssueCategory = 'bug' | 'feedback' | 'feature' | 'other';
export type AppIssueStatus = 'open' | 'in_progress' | 'resolved' | 'closed';
export type AppIssuePlatform = 'ios' | 'android' | 'web' | 'unknown';

export interface AppIssueTicket {
  id: string;
  reporter_id?: string | null;
  reporter_name: string;
  reporter_email?: string | null;
  subject: string;
  description: string;
  category: AppIssueCategory;
  status: AppIssueStatus;
  app_version?: string | null;
  platform: AppIssuePlatform;
  source: 'mobile' | 'webapp';
  created_at?: string | null;
  updated_at?: string | null;
}

export interface AppIssueTicketMessage {
  id: string;
  ticket_id: string;
  author_id?: string | null;
  author_name: string;
  body: string;
  created_at?: string | null;
}

export type AppIssueAgentIntent = 'code_fix' | 'communication' | 'unclear';
export type AppIssueAgentWorkflowStatus =
  | 'analyzing'
  | 'awaiting_approval'
  | 'rejected'
  | 'implementing'
  | 'merged'
  | 'failed'
  | 'communication_only';
export type AppIssueAgentRepo =
  | 'ChurchApp'
  | 'churchwebapp'
  | 'IMHIM_web'
  | 'classroom'
  | 'ncof_website';

export interface AppIssueTicketAgentJob {
  id: string;
  ticket_id: string;
  intent?: AppIssueAgentIntent | null;
  workflow_status: AppIssueAgentWorkflowStatus;
  target_repo?: AppIssueAgentRepo | null;
  target_repo_url?: string | null;
  overview?: string | null;
  suggested_fix?: string | null;
  suggested_reply?: string | null;
  confidence?: 'high' | 'medium' | 'low' | null;
  cursor_agent_id?: string | null;
  cursor_analyze_run_id?: string | null;
  cursor_implement_run_id?: string | null;
  feature_branch?: string | null;
  merge_sha?: string | null;
  error?: string | null;
  approved_by?: string | null;
  approved_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
}

const TICKET_QUERY_KEY = ['app-issue-tickets'];
const OPEN_COUNT_QUERY_KEY = ['app-issue-tickets-open-count'];
const AGENT_JOBS_QUERY_KEY = ['app-issue-ticket-agent-jobs'];
const messagesQueryKey = (ticketId: string) => ['app-issue-ticket-messages', ticketId];

const ACTIVE_AGENT_STATUSES: AppIssueAgentWorkflowStatus[] = ['analyzing', 'implementing'];

export const formatAppIssueAgentStatusLabel = (status?: AppIssueAgentWorkflowStatus | null) => {
  if (status === 'analyzing') return 'Analyzing';
  if (status === 'awaiting_approval') return 'Approve fix';
  if (status === 'communication_only') return 'Needs reply';
  if (status === 'implementing') return 'Implementing';
  if (status === 'merged') return 'Merged';
  if (status === 'failed') return 'Failed';
  if (status === 'rejected') return 'Rejected';
  return '—';
};

export const canApproveAppIssueAgentJob = (job?: AppIssueTicketAgentJob | null) =>
  job?.workflow_status === 'awaiting_approval' &&
  Boolean(job.target_repo) &&
  (job.intent === 'code_fix' || job.intent === 'unclear');

export const useAppIssueTickets = (enabled = true) =>
  useQuery<AppIssueTicket[]>({
    queryKey: TICKET_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_issue_tickets')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(500);
      if (error) throw error;
      return (data as AppIssueTicket[]) ?? [];
    },
    enabled,
    staleTime: 30_000,
  });

export const useOpenAppIssueTicketCount = (enabled = true) =>
  useQuery<number>({
    queryKey: OPEN_COUNT_QUERY_KEY,
    queryFn: async () => {
      const { count, error } = await supabase
        .from('app_issue_tickets')
        .select('id', { count: 'exact', head: true })
        .in('status', ['open', 'in_progress']);
      if (error) throw error;
      return count ?? 0;
    },
    enabled,
    staleTime: 30_000,
  });

export const useAppIssueTicketMessages = (ticketId?: string | null) =>
  useQuery<AppIssueTicketMessage[]>({
    queryKey: messagesQueryKey(ticketId ?? ''),
    queryFn: async () => {
      if (!ticketId) return [];
      const { data, error } = await supabase
        .from('app_issue_ticket_messages')
        .select('*')
        .eq('ticket_id', ticketId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return (data as AppIssueTicketMessage[]) ?? [];
    },
    enabled: Boolean(ticketId),
    staleTime: 15_000,
  });

export const useUpdateAppIssueTicket = () =>
  useMutation({
    mutationFn: async (payload: { id: string; status: AppIssueStatus }) => {
      const { data, error } = await supabase
        .from('app_issue_tickets')
        .update({ status: payload.status })
        .eq('id', payload.id)
        .select('*')
        .single();
      if (error) throw error;
      return data as AppIssueTicket;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TICKET_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: OPEN_COUNT_QUERY_KEY });
    },
  });

export const useAddAppIssueTicketMessage = () =>
  useMutation({
    mutationFn: async (payload: { ticketId: string; body: string; authorName: string; authorId: string }) => {
      const { data, error } = await supabase
        .from('app_issue_ticket_messages')
        .insert({
          ticket_id: payload.ticketId,
          author_id: payload.authorId,
          author_name: payload.authorName,
          body: payload.body,
        })
        .select('*')
        .single();
      if (error) throw error;
      return data as AppIssueTicketMessage;
    },
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: messagesQueryKey(variables.ticketId) });
    },
  });

export const useAppIssueTicketAgentJobs = (enabled = true) =>
  useQuery<AppIssueTicketAgentJob[]>({
    queryKey: AGENT_JOBS_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('app_issue_ticket_agent_jobs')
        .select('*')
        .order('updated_at', { ascending: false })
        .limit(500);
      if (error) {
        const missingTable =
          error.code === '42P01' ||
          error.code === 'PGRST205' ||
          /app_issue_ticket_agent_jobs/i.test(error.message);
        if (missingTable) return [];
        throw error;
      }
      return (data as AppIssueTicketAgentJob[]) ?? [];
    },
    enabled,
    staleTime: 10_000,
    refetchInterval: (query) => {
      const jobs = query.state.data ?? [];
      return jobs.some((job) => ACTIVE_AGENT_STATUSES.includes(job.workflow_status)) ? 8_000 : false;
    },
  });

export const useDecideAppIssueAgentJob = () =>
  useMutation({
    mutationFn: async (payload: { ticketId: string; action: 'approve' | 'reject' }) => {
      const { data, error } = await supabase.functions.invoke('approve-app-issue-fix', {
        body: payload,
      });
      if (error) {
        const message =
          (data && typeof data === 'object' && 'error' in data && typeof data.error === 'string'
            ? data.error
            : null) || error.message;
        throw new Error(message);
      }
      if (data && typeof data === 'object' && 'error' in data && typeof data.error === 'string') {
        throw new Error(data.error);
      }
      return data as { success: boolean; job?: AppIssueTicketAgentJob; skipped?: boolean };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TICKET_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: OPEN_COUNT_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: AGENT_JOBS_QUERY_KEY });
    },
  });
